import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Paper, CircularProgress, Button, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Tooltip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
} from '@mui/material';
import { Refresh, Router as RouterIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../services/api';

interface Trunk {
  id: string; name: string; host: string; port: number; protocol: string;
  status: string; max_channels: number; max_cps: number; srtp: string;
  tenant_name: string; tenant_status: string;
  active_calls: number; last_inbound_at: string | null; last_outbound_at: string | null;
  acl_ok: boolean;
}

const ago = (ts: string | null) => {
  if (!ts) return 'never';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
};

export const IngressTrunksPanel: React.FC = () => {
  const [rows, setRows] = useState<Trunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // edit-limits dialog state
  const [edit, setEdit] = useState<Trunk | null>(null);
  const [chans, setChans] = useState('');
  const [cps, setCps] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/tenants/ingress-trunks/all')
      .then(({ data }) => setRows(data || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEdit = (r: Trunk) => {
    setEdit(r);
    setChans(r.max_channels == null ? '' : String(r.max_channels));
    setCps(r.max_cps == null ? '' : String(r.max_cps));
    setSaveErr('');
  };
  const save = () => {
    if (!edit) return;
    setSaving(true);
    setSaveErr('');
    api.put(`/carriers/${edit.id}`, {
      max_channels: chans === '' ? null : Number(chans),
      cps_limit: cps === '' ? null : Number(cps),
    })
      .then(() => { setEdit(null); load(); })
      .catch((e) => setSaveErr(e.response?.data?.message || 'Save failed'))
      .finally(() => setSaving(false));
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const problems = rows.filter((r) => !r.acl_ok || r.status !== 'active').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <RouterIcon color="primary" />
        <Typography variant="h6">Customer IP Ingress Trunks</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={load} sx={{ ml: 'auto' }}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        IP-based customer trunks (authorised by source IP, not registration). A trunk is unreachable if its
        source IP is <b>not whitelisted</b> (ACL) or it is inactive. "Last inbound" going stale can indicate a
        connectivity issue on the customer's side. Use the pencil to edit a trunk's <b>channel</b> and <b>CPS</b> limits.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {problems > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{problems} trunk(s) need attention (not whitelisted or inactive).</Alert>}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell><TableCell>Trunk</TableCell><TableCell>Source IP : Port</TableCell>
                <TableCell>Transport</TableCell><TableCell>Status</TableCell><TableCell>IP Whitelisted</TableCell>
                <TableCell align="right">Channels</TableCell><TableCell align="right">CPS</TableCell><TableCell align="right">Active</TableCell>
                <TableCell>Last Inbound</TableCell><TableCell>Last Outbound</TableCell><TableCell align="center">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell><b>{r.tenant_name}</b></TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell><code>{r.host}:{r.port}</code></TableCell>
                  <TableCell>{r.protocol}{r.srtp && r.srtp !== 'none' ? ` / ${r.srtp}` : ''}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    {r.acl_ok
                      ? <Chip size="small" color="success" label="Allowed" />
                      : <Tooltip title="Source IP is NOT in the SIP ACL — calls from this trunk will be rejected (403)."><Chip size="small" color="error" label="NOT ALLOWED" /></Tooltip>}
                  </TableCell>
                  <TableCell align="right">{r.max_channels || '∞'}</TableCell>
                  <TableCell align="right">{r.max_cps || '∞'}</TableCell>
                  <TableCell align="right">{r.active_calls ? <Chip size="small" color="primary" label={r.active_calls} /> : 0}</TableCell>
                  <TableCell>{ago(r.last_inbound_at)}</TableCell>
                  <TableCell>{ago(r.last_outbound_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit channel / CPS limits">
                      <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={12}><i>No ingress trunks.</i></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!edit} onClose={() => !saving && setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Trunk Limits — {edit?.tenant_name} / {edit?.name}</DialogTitle>
        <DialogContent>
          {saveErr && <Alert severity="error" sx={{ mb: 2 }}>{saveErr}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Max Concurrent Channels" type="number" value={chans}
              onChange={(e) => setChans(e.target.value)} fullWidth
              inputProps={{ min: 0 }}
              helperText="Max simultaneous calls on this trunk. Blank = unlimited."
            />
            <TextField
              label="Max CPS (calls per second)" type="number" value={cps}
              onChange={(e) => setCps(e.target.value)} fullWidth
              inputProps={{ min: 0 }}
              helperText="Ingress call-attempts-per-second limit for this trunk. Blank = unlimited."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
